import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { Card, CardContent, Typography, TextField, Button, Box, Alert, CircularProgress } from '@mui/material';
import Logo from './Logo';

const EmailVerification = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [remainingTime, setRemainingTime] = useState(900); // 15 minutes in seconds
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const vehicleConflict = location.state?.vehicleConflict;
  const conflictMessage = location.state?.conflictMessage;

  useEffect(() => {
    if (!email) {
      navigate('/register');
      return;
    }

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      setError('');
      await apiService.resendVerificationCode(email);
      setRemainingTime(900); // Reset timer to 15 minutes
    } catch (err) {
      setError(err.message || 'Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!verificationCode) return;

    try {
      setLoading(true);
      setError('');
      const response = await apiService.verifyEmail(email, verificationCode);
      
      // Check if there's a vehicle conflict warning
      if (response?.vehicleConflict) {
        navigate('/login', { 
          state: { 
            message: 'Email verified successfully. You can now log in.',
            warning: response.conflictMessage || `Your vehicle is currently assigned to another user (${response.conflictUsername}). Please speak to your service manager to resolve this conflict.`
          }
        });
      } else {
        navigate('/login', { 
          state: { message: 'Email verified successfully. You can now log in.' }
        });
      }
    } catch (err) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <Logo height="80px" />
          </Box>
          
          <Typography variant="h5" component="h1" gutterBottom>
            Verify Your Email
          </Typography>
          
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            We've sent a verification code to<br />
            <strong>{email}</strong>
          </Typography>

          {vehicleConflict && conflictMessage && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Vehicle Assignment Notice
              </Typography>
              <Typography variant="body2">
                {conflictMessage}
              </Typography>
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              margin="normal"
              error={!!error}
              helperText={error}
              disabled={loading || remainingTime === 0}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || !verificationCode || remainingTime === 0}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify'}
            </Button>
          </form>

          {remainingTime > 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Code expires in: {formatTime(remainingTime)}
            </Typography>
          ) : (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Verification code has expired
            </Alert>
          )}

          <Button
            onClick={handleResendCode}
            disabled={loading || remainingTime > 0}
            sx={{ mt: 2 }}
          >
            Resend Code
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmailVerification;